import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { TmsTaskCardItem } from '../components/TmsTaskCardItem';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsWarehouseOperationTask } from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { TmsDepartureForm } from './components/TmsDepartureForm';
import { useTmsTaskOperations } from '../../tms/store/transportStore';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';
import { Toast } from 'antd-mobile';
import { TmsOperationSourceEnum, TmsTaskWaypointNodeType } from '../../../enums/TmsEnum';
import { FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';
import { translateResultCode } from '../../../utils/resultCode';

interface TaskData {
  taskId: string;
  routeName: string;
  waypointStatusId: number;
  waypointType: number;
  taskWaypointId: string;
  task?: TmsWarehouseOperationTask;
}

export default function TmsDeparturePage() {
  const location = useLocation();
  const { backRoute, ...taskData } = location.state as TmsPageState;
  const navigate = useNavigate();
  const { updateTmsTaskWaypointToDeparture } = useTmsTaskOperations();
  const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

  // 处理提交
  const handleSubmit = async (
    remark: string,
    departurePhotos: string[],
    departureSealPhotos: string[],
    firstPhotoLocation?: FirstPhotoWatermarkLocation | null
  ) => {
    if (!taskData) {
      Toast.show({
        content: t('pda.pages.tms.departure.noTaskData') || 'No task data',
        position: 'center',
        duration: 2000
      });
      return;
    }

    // 检查发车凭证照片数量（至少1张）
    if (departurePhotos.length < 1 || departureSealPhotos.length < 1) {
      Toast.show({
        content: t('pda.pages.tms.departure.photoDescription') || 'Please upload photos',
        position: 'center',
        duration: 2000
      });
      return;
    }

    try {
      // 上传照片到S3
      const uploadDeparturePromises = departurePhotos.map(async (base64Photo) => {
        // 确保base64格式正确
        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
        // 转换为File对象
        const file = await base64ToFile(formattedBase64, `departure_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
        // 上传到S3
        const result = await fileUpload(file, 'tms_departure_photo');
        return result.objectKey;
      });

      const uploadSealPromises = departureSealPhotos.map(async (base64Photo) => {
        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
        const file = await base64ToFile(formattedBase64, `departure_seal_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
        const result = await fileUpload(file, 'tms_departure_seal_photo');
        return result.objectKey;
      });

      const [departurePhotosS3Keys, departureSealPhotosS3Keys] = await Promise.all([
        Promise.all(uploadDeparturePromises),
        Promise.all(uploadSealPromises)
      ]);
      console.log('Successfully uploaded departure photos:', departurePhotosS3Keys, 'seal photos:', departureSealPhotosS3Keys);

      // 调用更新发车状态接口
      const taskId = parseInt(taskData.taskId);
      const taskWaypointId = parseInt(taskData.taskWaypointId);

      if (isNaN(taskId) || isNaN(taskWaypointId)) {
        throw new Error(t('pda.pages.tms.departure.invalidTaskId') || 'Invalid task ID or waypoint ID');
      }

      await saveTmsTaskWaypointLocationInfo(taskId, taskWaypointId, TmsTaskWaypointNodeType.DEPARTURE, firstPhotoLocation);

      const result = await updateTmsTaskWaypointToDeparture({
        tmsTaskWayPointId: taskWaypointId,
        taskId: taskId,
        departurePhotosJson: departurePhotosS3Keys,
        departureSealPhotosJson: departureSealPhotosS3Keys,
        departureRemark: remark || undefined,
        operationSource: TmsOperationSourceEnum.PDA,
      });

      if (result.success) {
        Toast.show({
          content: t('pda.common.submitSuccess') || 'Submit successful',
          position: 'center',
          duration: 2000
        });
        // 返回上一页：优先使用传入的 backRoute，否则回退一页
        if (backRoute) {
          navigate(backRoute);
        } else {
          navigate(-1);
        }
      } else {
        const errorMsg = translateResultCode(result.code, result.message) || t('pda.common.submitFailed') || 'Submit failed';
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('提交失败:', error);
      const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey as string | undefined;
      const displayMessage = messageKey
        ? t(messageKey)
        : (error instanceof Error ? error.message : String(error));
      Toast.show({
        content: displayMessage || t('pda.common.submitFailed') || 'Submit failed',
        position: 'center',
        duration: 2000
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-col gap-2 pt-2">
      <Header title={t('pda.pages.shipOut.departure')} backRoute={backRoute} />
      <div className="p-3 bg-gray-50 flex flex-col gap-4 flex-1">
        {taskData?.task && <TmsTaskCardItem task={taskData.task} showButtons={false} />}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          {taskData && (
            <TmsDepartureForm onSubmit={handleSubmit} />
          )}
        </div>
      </div>
    </div>
  );
}
